import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import NewVersionService from "ember-cli-new-version/services/new-version";

/**
 * This class implements the logic of our app when a new version of the front is detected.
 * It relies on the ember-cli-new-version addon which regularly fetches a VERSION.txt file at the root of the server.
 * This file contains the version of the app. If the content is different from the version of the running app, the page is reloaded.
 *
 * The VERSION.txt file is automatically created by ember-cli-new-version.
 * To test the feature on your machine, create your own VERSION.txt file in lib/core/public.
 * This will override the file created at build time by ember-cli-new-version.
 */
export default class SemeiaNewVersionService extends NewVersionService {
  @service declare router: RouterService;

  autoreloadRoutes = new Set([
    "login.index",
    "login.medic-password",
    "medics-web.protected.index.alerts-and-notifications",
    "medics-web.protected.index.tasks-and-consultations",
    "medics-web.protected.index.patient-list",
    "login.patient-link",
    "patient-web.protected.dashboard.index"
  ]);

  constructor(owner: unknown) {
    super(owner);
    this.router.on("routeDidChange", this._automaticReloadIfNeeded.bind(this));
  }

  onNewVersion(): void {
    this._automaticReloadIfNeeded();
  }

  reload(): void {
    window.location.reload();
  }

  private _automaticReloadIfNeeded() {
    if (this.isNewVersionAvailable) {
      const currentRouteName = this.router.currentRouteName;
      if (currentRouteName && this.autoreloadRoutes.has(currentRouteName)) {
        this.reload();
      }
    }
  }
}

declare module "@ember/service" {
  interface Registry {
    "new-version": SemeiaNewVersionService;
  }
}
